import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { getUserProfile, getCorrectDashboardRoute, UserProfile } from "@/lib/auth-utils";
import { useToast } from "@/hooks/use-toast";
import { LoadingSpinner } from "./LoadingSpinner";

interface AuthGuardProps {
  children: React.ReactNode;
  requiredRole?: 'student' | 'mentor' | 'admin';
  allowedRoles?: Array<'student' | 'mentor' | 'admin'>;
}

export const AuthGuard = ({ children, requiredRole, allowedRoles }: AuthGuardProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const isCheckingRef = useRef(false);
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    console.log('🚀 AuthGuard useEffect triggered, requiredRole:', requiredRole);
    
    // Reset refs on dependency change
    isCheckingRef.current = false;
    hasCheckedRef.current = false;
    
    // 15s timeout to prevent infinite loading
    const authTimeout = setTimeout(() => {
      console.error('⏰ AuthGuard timeout - forcing completion');
      setIsLoading(false);
      setIsAuthorized(true);
      console.warn('⚠️ Authentication timeout bypassed - allowing access');
    }, 15000);
    
    const checkAuth = async (session: any) => {
      // Prevent concurrent checks
      if (isCheckingRef.current) {
        console.log('⏭️ checkAuth already in progress, skipping');
        return;
      }
      isCheckingRef.current = true;
      
      console.log('🔍 checkAuth called with session:', session ? 'EXISTS' : 'NULL');
      try {
        if (!session?.user) {
          console.log('❌ No session found, redirecting...');
          if (requiredRole === 'student') {
            navigate('/auth/student');
          } else if (requiredRole === 'mentor' || requiredRole === 'admin') {
            navigate('/auth/mentor');
          } else {
            navigate('/');
          }
          return;
        }

        // Check "Remember me" preference
        const rememberMe = localStorage.getItem('ascendry_remember_me') === 'true';
        const browserSessionActive = sessionStorage.getItem('ascendry_browser_session');
        
        if (!rememberMe && !browserSessionActive) {
          console.log('🟢 Starting non-persistent browser session');
          sessionStorage.setItem('ascendry_browser_session', 'true');
        }
        
        if (!browserSessionActive) {
          sessionStorage.setItem('ascendry_browser_session', 'true');
        }

        // Get user profile with 10s timeout (user already validated in auth page)
        console.log('🔍 Fetching profile for user:', session.user.id);
        
        let profile: UserProfile | null = null;
        try {
          profile = await Promise.race([
            getUserProfile(session.user.id),
            new Promise<never>((_, reject) => 
              setTimeout(() => reject(new Error('Profile fetch timeout')), 10000)
            )
          ]);
          console.log('📋 Profile fetched:', profile);
        } catch (timeoutError) {
          console.error('⏰ Profile fetch timeout:', timeoutError);
          // Allow access on timeout - better than blocking
          console.warn('⚠️ Profile timeout - allowing access anyway');
          setIsAuthorized(true);
          setIsLoading(false);
          return;
        }
        
        if (!profile) {
          console.warn('⚠️ No profile found - allowing access anyway');
          setIsAuthorized(true);
          setIsLoading(false);
          return;
        }

        console.log('✅ Profile loaded successfully:', profile.role, profile.is_active);

        // Check role authorization (admins can access all dashboards)
        if (requiredRole && profile.role !== requiredRole && profile.role !== 'admin') {
          const correctRoute = getCorrectDashboardRoute(profile.role);
          toast({
            title: "Redirected",
            description: `You've been redirected to your ${profile.role} dashboard.`,
          });
          navigate(correctRoute);
          return;
        }

        if (allowedRoles && !allowedRoles.includes(profile.role) && profile.role !== 'admin') {
          const correctRoute = getCorrectDashboardRoute(profile.role);
          toast({
            title: "Access Denied",
            description: `This page is not available for your role. Redirecting to your dashboard.`,
            variant: "destructive",
          });
          navigate(correctRoute);
          return;
        }

        // Check if account is active
        if (!profile.is_active) {
          toast({
            title: "Account Inactive",
            description: "Your account has been deactivated. Please contact support.",
            variant: "destructive",
          });
          navigate('/');
          return;
        }

        console.log('✅ All checks passed, authorizing access');
        setIsAuthorized(true);
      } catch (error) {
        console.error('💥 Auth check error:', error);
        toast({
          title: "Authentication Error",
          description: "There was an error checking your authentication. Please try again.",
          variant: "destructive",
        });
        navigate('/');
      } finally {
        clearTimeout(authTimeout);
        isCheckingRef.current = false;
        hasCheckedRef.current = true;
        setIsLoading(false);
        console.log('🏁 AuthGuard check complete');
      }
    };

    // Initial session check
    console.log('🔄 Getting initial session...');
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('📦 Initial session retrieved:', session ? 'EXISTS' : 'NULL');
      checkAuth(session);
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('🔥 AuthGuard: Auth state change:', event);
        
        // Only redirect on explicit sign-out, not transient null-session events
        // (tab-switch can briefly fire events with null session during token refresh)
        if (event === 'SIGNED_OUT') {
          setIsLoading(true);
          setIsAuthorized(false);
          hasCheckedRef.current = false;
          
          if (requiredRole === 'student') {
            navigate('/auth/student');
          } else if (requiredRole === 'mentor' || requiredRole === 'admin') {
            navigate('/auth/mentor');
          } else {
            navigate('/');
          }
          return;
        }
        
        // Ignore other events with null session (transient during refresh)
        if (!session) {
          console.log('⏭️ Ignoring event with null session:', event);
          return;
        }
        
        // Skip if already authorized and just refreshing token
        if (event === 'TOKEN_REFRESHED' && isAuthorized) {
          return;
        }
        
        // Skip SIGNED_IN if we already checked (prevents double-fire)
        if (event === 'SIGNED_IN' && hasCheckedRef.current) {
          console.log('⏭️ Skipping SIGNED_IN - already checked');
          return;
        }
        
        if (session && event === 'SIGNED_IN') {
          setIsLoading(true);
          setIsAuthorized(false);
          setTimeout(() => checkAuth(session), 0);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [requiredRole, allowedRoles, navigate, toast]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingSpinner 
          text="Verifying access..." 
          subtext="Checking your permissions"
        />
      </div>
    );
  }

  if (!isAuthorized) {
    return null;
  }

  return <>{children}</>;
};
